import * as stylex from "@stylexjs/stylex";
import { Clock as ClockIcon, CloudSun, type LucideIcon, MapPin, MapPinOff } from "lucide-react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import type { WeatherLocation } from "@/domain/schemas";
import { type Clock, clockAt } from "@/domain/time";
import { timeZoneName } from "@/domain/zoneName";
import { PlaceForm, type WeatherSetupFlow } from "@/features/weather";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { BackLink } from "@/ui/BackLink";
import { ColorBlock } from "@/ui/ColorBlock";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { settingsLayout } from "../layout";
import { placeLabel } from "../location";
import { ZoneConfirm, type ZoneExample } from "./ZoneConfirm";
type LocationViewProps = {
  location: WeatherLocation | undefined;
  now: Date;
  clock: Clock;
  setup: WeatherSetupFlow;
  example?: ZoneExample | undefined;
  breakpoint: Breakpoint;
  onBack: () => void;
};
const styles = {
  grid: {
    k1xSpc: "xrvj5dj",
    kOIVth: "x1ur044a",
    kGNEyG: "x7a106z",
    $$css: true
  },
  card: {
    kGVxlE: "x98nf4m",
    $$css: true
  },
  sets: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    $$css: true
  }
};
type CurrentProps = Pick<LocationViewProps, "location" | "now" | "clock">;
export function LocationView({
  location,
  now,
  clock,
  setup,
  example,
  breakpoint,
  onBack
}: LocationViewProps) {
  const {
    state
  } = setup;
  return <div {...stylex.props(styles.grid, settingsLayout(breakpoint))}>
      <BackLink label={copy.location.back} onBack={onBack} />
      <div className="x78zum5 xdt5ytf x1ur044a xeuugli xh540kj xwe7p7i x16krsup x9f619">
        <h2 className="x1ghz6dp x4c74qz x43273g xh92tn4 xju0f8q">{copy.location.title}</h2>
        <Current location={location} now={now} clock={clock} />
        {state.step === "confirm-zone" && location?.timeZone ? <ZoneConfirm place={state.place} from={location.timeZone} now={now} clock={clock} example={example} onConfirm={setup.confirm} onKeep={setup.keep} /> : <Change located={location !== undefined} setup={setup} />}
      </div>
      <WhatItSets zone={location?.timeZone} />
    </div>;
}
function zoneLine({
  location,
  now,
  clock
}: CurrentProps): string {
  const zone = location?.timeZone;
  if (!zone) return copy.location.deviceClock;
  return copy.location.zoneNow(timeZoneName(zone), clockAt(now.toISOString(), clock, zone));
}
function Current(props: CurrentProps) {
  const {
    location
  } = props;
  return <div className="x78zum5 x6s0dn4 xddi9bj x5r8ks9 x1fv5t7x x1qhigcl">
      <span className="x78zum5 x1okw0bk">
        <Icon icon={location ? MapPin : MapPinOff} size={28} strokeWidth={1.5} />
      </span>
      <div className="x78zum5 xdt5ytf xt4j9g6 xeuugli">
        <Eyebrow>{copy.location.nowSetTo}</Eyebrow>
        <span className="xneq65o x1v94iar xrg9stw x1uo3zyz">
          {location ? placeLabel(location) : copy.location.notSet}
        </span>
        <span className="x1j61zf2 x15i68ds x1fzhlzt">{zoneLine(props)}</span>
      </div>
    </div>;
}
function Change({
  located,
  setup
}: {
  located: boolean;
  setup: WeatherSetupFlow;
}) {
  return <div className="x78zum5 xdt5ytf x1hu4l6k">
      <Eyebrow>{located ? copy.location.change : copy.location.set}</Eyebrow>
      <PlaceForm state={setup.state} canLocate={setup.canLocate} submitLabel={copy.location.search} onType={setup.type} onSubmit={setup.submit} onPick={setup.pick} onLocate={setup.locate} xstyle={styles.card} />
    </div>;
}
function SetsTile({
  icon,
  title,
  body
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return <div className="x78zum5 x6s0dn4 xddi9bj xh540kj x1fv5t7x x1xckggx x17qtykl">
      <span className="x78zum5 x1okw0bk">
        <Icon icon={icon} size={28} strokeWidth={1.5} />
      </span>
      <div className="x78zum5 xdt5ytf xt4j9g6 xeuugli">
        <span className="x1iig92y xhxcbl2 x1hrmo45">{title}</span>
        <span className="x14c3fy2 x1dld7ex x1fzhlzt">{body}</span>
      </div>
    </div>;
}
function WhatItSets({
  zone
}: {
  zone: string | undefined;
}) {
  const text = copy.location;
  const name = zone && timeZoneName(zone);
  return <ColorBlock tone="soft" padding="clamp(20px, 2.4vw, 32px)" xstyle={styles.sets}>
      <Eyebrow>{text.sets}</Eyebrow>
      <div className="x78zum5 xdt5ytf x1hu4l6k">
        <SetsTile icon={CloudSun} title={text.weather.title} body={text.weather.body} />
        <SetsTile icon={ClockIcon} title={text.timeZone.title} body={name ? text.timeZone.zoned(name) : text.timeZone.unset} />
      </div>
      <div className="x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">{name ? text.caption(name) : text.captionUnset}</div>
      <span className="xr0iwhn xhunfcg xkv8mnm xtvhhri">{text.shared}</span>
    </ColorBlock>;
}